/**
 * F22c Task 4: cross-tab coordination for device-key mutations.
 *
 * WHY: Two open tabs can interleave IDB writes (e.g. replenish in tab A,
 * drift-heal in tab B), producing a last-write-wins race that is the root cause
 * of the 2026-06-07 SPK-drift incident (see Task 0 findings in the F22c plan).
 *
 * withDeviceKeysLock  — browser-wide critical section (Web Locks when available,
 *                       in-tab promise-chain mutex otherwise). Callers MUST NOT
 *                       call withDeviceKeysLock while already inside a
 *                       withDeviceKeysLock callback — navigator.locks does NOT
 *                       re-enter and would deadlock. Current call sites (only
 *                       saveDeviceKeys) are all top-level; no nesting exists.
 *
 * notifyDeviceKeysChanged — broadcast to sibling tabs "deviceKeys changed".
 * onDeviceKeysChanged     — subscribe. Subscriber in useCrypto.ts guards
 *                           `changedId !== current` so same-deviceId echoes
 *                           (BroadcastChannel echoes to sender in test stubs,
 *                           but NOT in real browsers) are no-ops.
 *
 * Both APIs are null-safe: degrade gracefully in jsdom/exotic browsers that
 * lack BroadcastChannel (navigator.locks absence falls through to in-tab mutex).
 */

const LOCK_NAME = "kollegeplus-devicekeys";
const CHANNEL_NAME = "kollegeplus-devicekeys";

/** BroadcastChannel for cross-tab device-key change notifications. Null when
 * the API is unavailable (e.g. jsdom without stub, old Safari). */
let channel: BroadcastChannel | null = null;
try {
  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }
} catch {
  // Defensive: some environments throw on BroadcastChannel construction.
  channel = null;
}

/** In-tab promise-chain mutex — fallback when navigator.locks is unavailable.
 * The chain itself never rejects so subsequent tasks are always unblocked. */
let inTabChain: Promise<unknown> = Promise.resolve();

/**
 * Run `fn` inside a browser-wide lock (Web Locks API) or, when unavailable,
 * behind an in-tab promise-chain mutex.  Either way, concurrent calls to
 * `withDeviceKeysLock` with the same lock name are serialised — one at a time.
 *
 * WARNING: Do NOT call `withDeviceKeysLock` from inside a `withDeviceKeysLock`
 * callback.  navigator.locks does NOT support re-entrancy and would deadlock.
 * All current call sites (saveDeviceKeys) are top-level; document any future
 * callers to uphold this invariant.
 */
export async function withDeviceKeysLock<T>(fn: () => Promise<T>): Promise<T> {
  if (
    typeof navigator !== "undefined" &&
    navigator.locks != null &&
    typeof navigator.locks.request === "function"
  ) {
    return navigator.locks.request(LOCK_NAME, fn) as Promise<T>;
  }
  // Fallback: in-tab promise-chain mutex.
  // `run` always settles (resolves or rejects) with fn()'s outcome.
  // `inTabChain` is updated to a version that swallows errors so the chain
  // stays alive regardless of whether fn() throws.
  const run = inTabChain.then(fn, fn);
  inTabChain = run.catch(() => {});
  return run;
}

/** Payload broadcast on a device-key write. `spkKeyId` lets a sibling tab detect
 * a key-material change even when the deviceId is unchanged (two tabs sharing one
 * deviceId but with divergent SPK — the drift-loop case). Optional for backwards
 * compatibility with callers/tests that only pass a deviceId. */
export interface DeviceKeysChangedEvent {
  deviceId: string;
  spkKeyId?: number;
}

/**
 * Broadcast to sibling tabs that the device-keys for `deviceId` have been
 * written.  No-op when BroadcastChannel is unavailable.
 *
 * `spkKeyId` (the signed-pre-key id of the just-written bundle) lets subscribers
 * resync when the MATERIAL changed under the SAME deviceId — guarding on deviceId
 * alone missed the two-tabs-same-id drift loop.
 *
 * NOTE: BroadcastChannel does NOT echo to the sending tab in real browsers,
 * but test stubs may echo.  Subscribers must guard against acting on their own
 * current (deviceId, spkKeyId).
 */
export function notifyDeviceKeysChanged(deviceId: string, spkKeyId?: number): void {
  channel?.postMessage({ deviceId, spkKeyId });
}

/**
 * Subscribe to device-key change notifications from other tabs.  The callback
 * receives `{ deviceId, spkKeyId? }` — the bundle written by the other tab.
 *
 * Call site (useCrypto.ts) resyncs when the deviceId differs OR the spkKeyId
 * differs for the same deviceId, so a sibling tab that rotated/healed its SPK no
 * longer leaves this tab publishing stale material (which the server would flag
 * as drift on the next heartbeat, looping the reinit).
 *
 * No-op when BroadcastChannel is unavailable.
 */
export function onDeviceKeysChanged(cb: (ev: DeviceKeysChangedEvent) => void): void {
  channel?.addEventListener("message", (e: MessageEvent) => cb(e.data as DeviceKeysChangedEvent));
}

/**
 * FOR TESTS ONLY — reset module-level state between test cases.
 * Closes the existing BroadcastChannel, creates a fresh one (so listeners from
 * the previous test don't bleed into the next), and resets the in-tab mutex.
 *
 * Exported under a leading-underscore convention to signal test-only usage.
 */
export function _resetTabSyncForTests(): void {
  try {
    channel?.close();
  } catch { /* ignore */ }
  channel = null;
  try {
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(CHANNEL_NAME);
    }
  } catch { /* ignore */ }
  inTabChain = Promise.resolve();
}
